import React, { useState } from 'react';
import { Modal, Input, Button, Upload, App, Form } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import axios from 'axios';
import { tc, tns as t } from '../../i18n/common';
import { buildReportWorkbook, buildImportWorkbook, workbookToBlob } from './Shared/orgExport';

const { TextArea } = Input;
const { Dragger } = Upload;

import { useNavigate } from 'react-router-dom';

const TaskCreationModal = ({ visible, onClose, changeLog, chartData, allRawUsers = {} }) => {
    // const { t } = useTranslation('common');
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const formData = new FormData();
            const defaultDesc = (changeLog && changeLog.length > 0) 
                ? `Change Request (${changeLog.length} changes)` 
                : 'Task Request';
            formData.append('description', values.description || defaultDesc);
            
            // Auto-attach change log as JSON
            if (changeLog && changeLog.length > 0) {
                formData.append('changes', JSON.stringify(changeLog));
            }

            // Attach two purpose-built spreadsheets, both built from the shared
            // exporter so they always agree with the in-app change log:
            //   1. A human report (Changes sheet + marked user list) so the admin
            //      knows exactly what to change.
            //   2. The re-importable file, so the change can be replayed/visualised
            //      back in the organigram.
            if (chartData) {
                const report = buildReportWorkbook({ chartData, changeLog, t });
                formData.append('files[]', workbookToBlob(report), 'Organigram_Changes_Report.xlsx');

                const importFile = buildImportWorkbook({ chartData, allRawUsers, changeLog, t });
                formData.append('files[]', workbookToBlob(importFile), 'Organigram_Import.xlsx');
            }

            // Append manual files
            fileList.forEach(file => {
                formData.append('files[]', file.originFileObj);
            });

            // Calculate Client Domain (Most frequent domain in chartData)
            let clientDomain = null;
            if (chartData) {
                const domains = {};
                chartData.forEach(d => {
                    const list = [...(d.lists?.leaders || []), ...(d.lists?.mitarbeiter || []), ...(d.lists?.viewers || [])];
                    list.forEach(u => {
                        if (u.mail && u.mail.includes('@')) {
                            const dom = u.mail.split('@')[1].toLowerCase();
                            domains[dom] = (domains[dom] || 0) + 1;
                        }
                    });
                });
                if (Object.keys(domains).length > 0) {
                    clientDomain = Object.entries(domains).reduce((a, b) => b[1] > a[1] ? b : a)[0];
                }
            }
            if (clientDomain) {
                formData.append('client_domain', clientDomain);
            }

            await axios.post('/tasks', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            message.success(tc('tasks.success_msg'));
            form.resetFields();
            setFileList([]);
            onClose();
            navigate('/organigram/tasks'); // Redirect to My Tasks
        } catch (error) {
            console.error('Error creating task:', error);
            message.error(tc('common.error_occurred') || 'Failed to send request');
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: (file) => {
            setFileList([...fileList, file]);
            return false;
        },
        fileList,
    };

    return (
        <Modal
            title={tc('tasks.create_modal_title')}
            open={visible}
            onOk={handleOk}
            onCancel={onClose}
            confirmLoading={loading}
            okText={tc('tasks.send_btn')}
            cancelText={tc('organigram_ui.back')}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="description"
                    label={tc('tasks.description_label')}
                    rules={[{ required: false }]}
                >
                    <TextArea rows={4} placeholder={tc('tasks.description_placeholder')} />
                </Form.Item>

                {/* Attachments removed as per request (auto-attached) */}
            </Form>
        </Modal>
    );
};

export default TaskCreationModal;
